import { useEffect, useRef, useState } from '@wordpress/element';

const SMOOTH_CHARS_PER_FRAME = 4;

/**
 * Progressively reveals streamed text to create a smooth "typing" effect in the UI.
 *
 * WHY WE NEED IT:
 * MarkdownTextPrimitive has a smooth typing effect that we disable because it breaks Markdown rendering.
 * If a markdown link like `[Link](https...)` is streamed one character at a time, the user sees raw,
 * broken syntax flash on screen until the final parenthesis arrives.
 *
 * WHAT IT DOES:
 * Acts as a look-ahead buffer. Reveals standard text at a fixed rate (4 chars/frame). When it encounters
 * a markdown trigger character (`[`, `*`, `~`, `` ` ``), it checks if the complete construct exists in
 * the buffer and reveals the entire block atomically, preventing visual flashing of raw syntax.
 */
export default function useMarkdownAwareSmooth( text: string, enabled: boolean ): string {
	const posRef = useRef( 0 );
	const [ displayText, setDisplayText ] = useState( '' );
	const rafRef = useRef< number >();

	useEffect( () => {
		if ( ! enabled ) {
			cancelAnimationFrame( rafRef.current! );
			posRef.current = text.length;
			setDisplayText( text );
			return;
		}

		if ( posRef.current >= text.length ) {
			return;
		}

		// Single combined regex for atomic markdown blocks
		const tokenRegex = /^(\[[^\]]*\]\([^)]*\)|\*{1,3}[^*]+\*{1,3}|`[^`]+`|~~[^~]+~~|#{1,6} .+|\[>\d+<\])/;

		const step = () => {
			let next = posRef.current;

			for ( let i = 0; i < SMOOTH_CHARS_PER_FRAME && next < text.length; i++ ) {
				const char = text[ next ];

				// Only perform expensive substring slicing and regex matching if we hit a trigger character
				if ( char === '[' || char === '*' || char === '`' || char === '~' || char === '#' ) {
					const remaining = text.substring( next );
					const match = remaining.match( tokenRegex );

					if ( match ) {
						next += match[ 0 ].length;
						break; // Jump over the atomic block immediately
					}
				}
				next++;
			}

			posRef.current = Math.min( next, text.length );
			setDisplayText( text.slice( 0, posRef.current ) );

			if ( posRef.current < text.length ) {
				rafRef.current = requestAnimationFrame( step );
			}
		};

		rafRef.current = requestAnimationFrame( step );
		return () => cancelAnimationFrame( rafRef.current! );
	}, [ text, enabled ] );

	return displayText;
}
